import * as React from "react"
import {
  type EntityCategory,
  type IntervalOrigin,
  type SummaryFilterInput,
} from "@digits-graphql/frontend/graphql-bearer"
import { SvgBookOpen01 } from "@digits-shared/components/SVGIcons/line/BookOpen01.svg"
import {
  Checkbox as FilterCheckbox,
  CheckboxContainer,
} from "@digits-shared/DesignSystem/FormElements/Checkbox"
import stringHelper from "@digits-shared/helpers/stringHelper"
import colors from "@digits-shared/themes/colors"
import fonts, { DetailText } from "@digits-shared/themes/typography"
import styled, { css } from "styled-components"
import {
  FilterAccordionContent,
  FilterAccordionHeader,
  FilterAccordionItem,
} from "src/shared/components/Filters/FilterAccordion"
import { useCategoryFilters } from "src/shared/components/Filters/hooks/useCategoryFilters"
import { FilterContentSection } from "src/shared/components/Filters/shared"
import { TransactionSearchBox } from "src/shared/components/Filters/TransactionSearchBox"
import { TransactionFilterType } from "src/shared/components/Filters/types"
import { isCategory } from "src/shared/helpers/hierarchicalCategoryHelper"
import type { ActiveHierarchicalDimensionNode } from "src/shared/helpers/hierarchicalDimensionHelper"

/*
 STYLES
*/

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Checkbox = styled(FilterCheckbox)``

const Count = styled(DetailText)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: ${colors.secondary10};
  border-radius: 32px;
  padding: 2px 8px;
  height: 24px;

  .root & {
    font-weight: ${fonts.weight.heavy};
  }
`

const CategoryHierarchyRowDisplayNumber = styled.div`
  font-weight: ${fonts.weight.normal};
  color: ${colors.secondary};
  font-size: 11px;
`

const ListItem = styled.div<{ depth?: number }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;

  ${CheckboxContainer} {
    opacity: 0;
  }

  .has-selection &,
  &:hover {
    ${CheckboxContainer} {
      opacity: 1;
    }
    ${Count} {
      display: none;
    }
  }

  ${({ depth }) =>
    depth !== undefined &&
    css`
      margin-left: ${depth * 12}px;
    `};
`

const ItemContents = styled.div`
  flex: 1;
`

const CategoryName = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 14px;
  font-weight: ${fonts.weight.medium};

  .root & {
    font-weight: ${fonts.weight.heavy};
  }
`

/*
 INTERFACES
*/

interface FilterByCategoryProps {
  filters: Omit<SummaryFilterInput, "viewKey">
  selectedCategories: Set<string>
  toggleCategory: (category: EntityCategory) => void
  origin?: IntervalOrigin
  skip?: boolean
}

interface ItemProps {
  count: number
  checked: boolean
  node: ActiveHierarchicalDimensionNode<EntityCategory>
  toggleCategory: (category: EntityCategory) => void
}

/*
 COMPONENTS
*/

export const FilterByCategory: React.FC<FilterByCategoryProps> = ({
  filters,
  selectedCategories,
  toggleCategory,
  origin,
  skip,
}) => {
  const [searchTerm, setSearchTerm] = React.useState<string>("")
  const { categoryNodes } = useCategoryFilters({
    filters,
    searchTerm,
    origin,
    skip,
  })

  const count = selectedCategories.size
  const className = stringHelper.classNames(!!count && "has-selection")

  return (
    <FilterAccordionItem value={TransactionFilterType.Category}>
      <FilterAccordionHeader Icon={SvgBookOpen01} count={count}>
        Category
      </FilterAccordionHeader>
      <FilterAccordionContent>
        <FilterContentSection>
          <TransactionSearchBox
            placeholder="Lookup…"
            onChange={setSearchTerm}
            onSubmit={setSearchTerm}
            onClear={setSearchTerm}
            initialValue={searchTerm}
          />
        </FilterContentSection>
        <FilterContentSection $scrollable $maxHeight={200}>
          <CategoryList className={className}>
            {categoryNodes.map((node) => (
              <CategoryListItem
                key={node.dimension.id}
                node={node}
                count={count}
                checked={selectedCategories.has(node.dimension.id)}
                toggleCategory={toggleCategory}
              />
            ))}
          </CategoryList>
        </FilterContentSection>
      </FilterAccordionContent>
    </FilterAccordionItem>
  )
}

const CategoryListItem: React.FC<ItemProps> = ({ node, checked, toggleCategory }) => {
  const displayNumber = React.useMemo(
    () =>
      isCategory(node.dimension)
        ? node.dimension.displayNumber
        : node.dimension.chartOfAccountsDisplayNumber,
    [node]
  )

  const toggleRow = React.useCallback(() => {
    toggleCategory(node.dimension)
  }, [node, toggleCategory])

  return (
    <ListItem
      depth={node.depth}
      onClick={toggleRow}
      className={stringHelper.classNames(node.depth === 0 && "root")}
    >
      <ItemContents>
        <CategoryName>
          {displayNumber && (
            <CategoryHierarchyRowDisplayNumber>{displayNumber}</CategoryHierarchyRowDisplayNumber>
          )}
          <div>{node.dimension.name}</div>
        </CategoryName>
      </ItemContents>

      <Checkbox checked={checked} onChange={toggleRow} />
      <Count>{node.count}</Count>
    </ListItem>
  )
}
